import { Box, Button, Typography, useTheme } from '@mui/material';
import { Trash2 } from 'react-feather';
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import IconFeather from 'src/components/common/Icon';
import DragAndDropFileBox from 'src/components/DragAndDropFileBox';
import FileIcon from 'src/components/Icons/File';
import FormBulkUploadFooter from 'src/features/AIHSCodeLabeling/FormBulkUploadFooter';
import { useNotification } from 'src/hooks/useNotification';
import { useMutateUploadFile } from 'src/services/mutate/useMutateAILabeling';
import { SUCCESS_CODE } from 'src/utils/constants/statusCode';
import { getStringOrDefault } from 'src/utils/helpers/stringHelpers';
import { useBulkUploadSearchStore } from 'src/zustand/useAILabelingStore';

const FormBulkUploadStep1 = () => {
  const {
    palette: { primary, textTertiary, divider },
    shape: { borderRadius },
  } = useTheme();
  const { isPending, mutateAsync } = useMutateUploadFile();

  const { dispatch, file } = useBulkUploadSearchStore();
  const { notification } = useNotification();

  const onChange = (e: File) => {
    dispatch(e, 'file');
  };

  const onDeleteFile = () => {
    dispatch(null, 'file');
  };

  const onUploadFile = async () => {
    if (!file) return;
    const res = await mutateAsync(file);

    notification(
      res,
      {
        title: 'File Upload Successful',
        description: 'Your file has been uploaded successfully.',
      },
      {
        title: 'File Upload Failed',
        description: res.message || 'There was an issue uploading your file.',
      },
    );

    if (res.status_code === SUCCESS_CODE.GET) {
      dispatch(getStringOrDefault(res.data?.file_id), 'fileId');
      dispatch(getStringOrDefault(res.data?.file_url), 'fileURL');
      dispatch(res.data?.all_columns, 'columns');
      dispatch(2, 'step');
      dispatch(res.data?.auto_map_product_name ?? '', 'productName');
      dispatch(res.data?.auto_map_product_description ?? '', 'productDescription');
    }
  };

  return (
    <Box>
      <Typography display={'inline'} color={primary.main} variant="h6">
        Step 1:{' '}
        <Typography display={'inline'} variant="h6" color={'text.primary'}>
          Upload Your Bills of Lading
        </Typography>
      </Typography>
      <Typography color={textTertiary.main} variant="body2">
        Kindly upload a .CSV or .XLSX file containing the Product Name or Product Description. Our system is optimized
        to work seamlessly with your standard Bill of Lading (B/L) file. It will automatically map the relevant fields
        and accurately predict the corresponding HS Code based on the provided product details.
      </Typography>

      <Box mt={3} />

      <DragAndDropFileBox
        type="file"
        description="Only support .xlsx, .csv file (max 1M)"
        accept={'.xlsx,.csv'}
        onChange={onChange}
      />

      {file && (
        <Box
          gap={1}
          mt={3}
          key={file?.name}
          display={'flex'}
          alignItems={'center'}
          borderRadius={borderRadius / 2}
          p={2}
          border={`1px solid ${divider}`}
        >
          <FileIcon fontSize="large" />
          <Typography flex={1} variant="caption">
            {file?.name}
          </Typography>
          <Button onClick={() => onDeleteFile()} color="error" size="small">
            <IconFeather icon={<Trash2 />} muiProps={{ color: 'inherit', fontSize: 'small' }} />
          </Button>
        </Box>
      )}

      <ButtonWithLoading
        onClick={onUploadFile}
        isLoading={isPending}
        disabled={!Boolean(file)}
        fullWidth
        sx={{ mt: 6, height: 42 }}
        size="large"
        isRounded
        variant="contained"
      >
        Map Field
      </ButtonWithLoading>

      <FormBulkUploadFooter />
    </Box>
  );
};

export default FormBulkUploadStep1;
